import * as React from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { FormProvider } from 'components/Form/Form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHistory, useLocation } from 'react-router-dom';
import axios, { AxiosError } from 'axios';

import {
  TempControlCalculationMethod,
  TempControlProject,
  TempControlProjectAuthorizationKey,
  TempControlValveFamily,
  UpdateTempControlProjectBody,
} from '__generated-api__';
import { AuthStatus, useAbility, useAuth } from 'auth';
import { useToast } from 'my-account/toast';

import ErrorList from 'tempcontrol/components/error-list';
import HistoryPanel from 'tempcontrol/components/history-panel';
import InfoCallout from 'tempcontrol/components/info-callout';
import { WizardFooter, WizardLayout } from 'tempcontrol/components/wizard-layout';
import WizardHero from 'tempcontrol/components/wizard-hero';
import { useTempControlHistory } from 'tempcontrol/hooks/use-tempcontrol-history';
import { useTempControlProject } from 'tempcontrol/hooks/use-tempcontrol-project';
import {
  clearActiveProjectKey,
  clearProjectStorage,
  readActiveProjectKey,
  writeActiveProjectKey,
} from 'tempcontrol/api/auth-keys';
import { AddOnOptionKey, facilityUsesFlowRate, wizardStepCopy, WizardStepId } from 'tempcontrol/enums';
import {
  projectToFormValues,
  step1ProjectSchema,
  step3ConfigurationSchema,
  step4FixturesSchema,
  WizardFormValues,
  wizardFormSchema,
} from 'tempcontrol/validations/schemas';
import { buildPortalProjectsUrl } from 'tempcontrol/utils/wizard-url';
import { parseCalculationMethod } from 'tempcontrol/utils/calculation-method';
import { defaultResultValveFamily, resolveResultProducts } from 'tempcontrol/utils/resolve-result-products';
import wordpressData from 'data';

import Step1Project from 'tempcontrol/steps/step-1-project';
import Step3Configuration from 'tempcontrol/steps/step-3-configuration';
import Step4Fixtures from 'tempcontrol/steps/step-4-fixtures';
import ResultsPage from 'tempcontrol/results/results-page';
import DownloadModal, { DownloadFormValues } from 'tempcontrol/results/download-modal';

const PROJECT_QUERY_PARAM = 'project';

type WizardPhase = 'wizard' | 'results';

/**
 * Top-level orchestrator for the public TempControl wizard.
 *
 * Visible structure (matches design):
 *   - 3 numbered wizard steps:
 *       1. Name your project      (project_name)
 *       2. Project configuration  (facility + PSI + flags + add-ons)
 *       3. Add your fixtures       (fixtures)
 *   - Results page (separate, not numbered) - rendered via `phase = 'results'`.
 *     The valve family (digital vs conventional) is chosen here, not as a step.
 *
 * Concerns this component owns:
 *   - One `useForm` instance shared across every step (loose schema).
 *     Per-step transition validation is enforced via the strict step
 *     schemas inside each `handleStepXNext` handler.
 *   - The active project (`?project={key}` URL param + localStorage resume).
 *   - Step state (1..4) + a separate `phase` flag for results.
 *   - Persistence: each "Next" PATCHes the slice of `inputs` that the
 *     current step owns, plus `current_step`.
 */
export const Wizard: React.VFC = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const projectApi = useTempControlProject();

  const [currentStep, setCurrentStep] = React.useState<WizardStepId>(1);
  const [highestStepReached, setHighestStepReached] = React.useState<WizardStepId>(1);
  const [phase, setPhase] = React.useState<WizardPhase>('wizard');
  const [project, setProject] = React.useState<TempControlProject | TempControlProjectAuthorizationKey | null>(null);
  const [isBootstrapping, setIsBootstrapping] = React.useState(true);
  const [bootstrapError, setBootstrapError] = React.useState<string | null>(null);
  const [stepErrors, setStepErrors] = React.useState<string[]>([]);
  const [calculationErrors, setCalculationErrors] = React.useState<string[]>([]);
  const [calculationMethod, setCalculationMethod] = React.useState<TempControlCalculationMethod>(
    TempControlCalculationMethod.Hunters
  );
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [downloadPrefill, setDownloadPrefill] = React.useState<Partial<DownloadFormValues>>({});
  const [isPdfRendering, setIsPdfRendering] = React.useState(false);
  const [selectedValveFamily, setSelectedValveFamily] = React.useState<TempControlValveFamily>(
    TempControlValveFamily.Digital
  );
  const draftHistory = useTempControlHistory();
  const auth = useAuth();
  const ability = useAbility();
  // "View all projects" link points at the customer-portal index, but
  // only for logged-in users with the `list` ability (every signed-in
  // role has it today; gating on CASL keeps the link in sync with
  // future role changes the way the nav dropdown does).
  const portalProjectsUrl = React.useMemo(() => {
    if (auth.status !== AuthStatus.LoggedIn) return undefined;
    if (!ability.can('list', 'TempControlProject')) return undefined;
    return buildPortalProjectsUrl({ mineOnly: true });
  }, [auth.status, ability]);

  const formMethods = useForm<WizardFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(wizardFormSchema),
    defaultValues: projectToFormValues(null),
  });

  /** Parse the project key out of the current URL, if present. */
  const projectKeyFromUrl = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get(PROJECT_QUERY_PARAM) ?? undefined;
  }, [location.search]);

  /**
   * Apply a freshly-loaded project to the wizard state: hydrate the
   * form, jump to the right step (results page if it's already
   * completed, otherwise resume at `current_step`), update the active
   * key in localStorage, and reflect the key in the URL so reloads
   * land the user in the same place.
   *
   * Used by both the initial bootstrap and the history panel's
   * "switch draft" handler so the two paths stay in lockstep.
   */
  const applyLoadedProject = React.useCallback(
    (loaded: TempControlProject, key: string) => {
      setProject(loaded);
      setCalculationMethod(parseCalculationMethod(loaded.method));
      formMethods.reset(projectToFormValues(loaded));
      const resumeStep = clampStep(loaded.current_step ?? 1);
      if (loaded.status === 'completed' && loaded.result) {
        setCurrentStep(3);
        setHighestStepReached(3);
        setPhase('results');
        const resolved = resolveResultProducts(loaded.result);
        if (resolved) {
          setSelectedValveFamily(
            defaultResultValveFamily(resolved, loaded.inputs?.family ?? loaded.valve_family ?? null)
          );
        }
      } else {
        setCurrentStep(resumeStep);
        setHighestStepReached(resumeStep);
        setPhase('wizard');
      }
      setStepErrors([]);
      setCalculationErrors([]);
      writeActiveProjectKey(key);
      setProjectQueryParam(history, location, key);
    },
    [formMethods, history, location]
  );

  /**
   * On mount, decide whether to auto-resume a previous project or
   * drop the visitor onto a fresh Step 1 with the History panel:
   *
   *  1. `?project=<key>` in the URL is treated as an explicit intent
   *     (shareable link, "Open in wizard" from the customer portal,
   *     same-tab reload after navigating into a step) and is always
   *     auto-resumed - even if the project is already completed,
   *     since the user clearly asked for it.
   *
   *  2. Without that query param, we only auto-resume an in-progress
   *     **draft** from localStorage. The "pick up where you left off"
   *     UX still works mid-session, but the wizard no longer ambushes
   *     the user with yesterday's results screen for a project they
   *     already finished. Completed projects show up in the History
   *     panel instead, one click away if the user actually wants to
   *     revisit the result.
   *
   *  3. With no candidate key at all, we show a brand-new wizard.
   */
  React.useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const isExplicitResume = typeof projectKeyFromUrl === 'string' && projectKeyFromUrl.length > 0;
      const candidateKey = projectKeyFromUrl ?? readActiveProjectKey();
      if (!candidateKey) {
        setIsBootstrapping(false);
        return;
      }
      try {
        const loaded = await projectApi.loadProject(candidateKey);
        if (cancelled) return;

        // Implicit resume of a finished project lands the user on
        // the results screen with no clear escape hatch; surface
        // the intro instead so the History panel can do its job.
        if (!isExplicitResume && loaded.status === 'completed') {
          return;
        }

        applyLoadedProject(loaded, candidateKey);
      } catch (e) {
        if (cancelled) return;
        // Stale or unauthorized key - clear it and start fresh.
        clearProjectStorage(candidateKey);
        setProject(null);
        setBootstrapError(formatErrorMessage(e, 'Could not load your previous project.'));
        clearProjectQueryParam(history, location);
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    };
    void bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Click handler for the History panel. Loads the chosen draft and
   * swaps it into the wizard. If the click lands on the currently-
   * active draft we no-op (the panel disables the active row's button
   * but this guards against any race).
   */
  const handleSwitchProject = React.useCallback(
    async (key: string) => {
      if (project?.key === key) return;
      setStepErrors([]);
      setCalculationErrors([]);
      setBootstrapError(null);
      try {
        const loaded = await projectApi.loadProject(key);
        applyLoadedProject(loaded, key);
      } catch (e) {
        // Treat 401/404 the same way the bootstrap does - the localStorage
        // entry is dead, drop it and let the history hook re-snapshot so
        // the row disappears from the panel on the next render.
        clearProjectStorage(key);
        setBootstrapError(formatErrorMessage(e, 'Could not open that project.'));
        void draftHistory.refresh();
      }
    },
    [applyLoadedProject, draftHistory, project, projectApi]
  );

  // ---- Step navigation handlers ----

  const goToStep = React.useCallback(
    (step: WizardStepId) => {
      setStepErrors([]);
      setCurrentStep(step);
      if (step > highestStepReached) {
        setHighestStepReached(step);
      }
      setPhase('wizard');
    },
    [highestStepReached]
  );

  const handleBack = React.useCallback(() => {
    if (currentStep <= 1) return;
    goToStep((currentStep - 1) as WizardStepId);
  }, [currentStep, goToStep]);

  /** PATCH a slice of inputs to the current project. */
  const patchProject = React.useCallback(
    async (body: UpdateTempControlProjectBody): Promise<TempControlProject> => {
      const key = project?.key;
      if (!key) {
        throw new Error('No active project to update.');
      }
      const updated = await projectApi.updateProject(key, body);
      setProject((prev) => (prev ? { ...prev, ...updated } : updated));
      return updated;
    },
    [project, projectApi]
  );

  // Step 1 - project name
  const handleStep1Next = React.useCallback(async () => {
    setStepErrors([]);
    const values = formMethods.getValues();
    const parsed = step1ProjectSchema.safeParse({ project_name: values.project_name });
    if (!parsed.success) {
      assignFieldErrors(formMethods, parsed.error.flatten().fieldErrors);
      return;
    }

    try {
      if (!project) {
        const result = await projectApi.createProject({ project_name: parsed.data.project_name });
        setProject(result.project);
        writeActiveProjectKey(result.project.key);
        setProjectQueryParam(history, location, result.project.key);
        // The newly-issued auth key just got persisted; let the
        // history panel pick the row up the next time the wizard
        // returns to Step 1 (or right now, if we're still here).
        void draftHistory.refresh();
      } else if (parsed.data.project_name !== project.project_name) {
        await patchProject({ project_name: parsed.data.project_name, current_step: 1 });
      }
      goToStep(2);
    } catch (e) {
      setStepErrors([formatErrorMessage(e, 'Could not save your project name.')]);
    }
  }, [draftHistory, formMethods, goToStep, history, location, patchProject, project, projectApi]);

  // Step 2 - merged configuration + options
  const handleStep2Next = React.useCallback(async () => {
    setStepErrors([]);
    const values = formMethods.getValues();
    const parsed = step3ConfigurationSchema.safeParse({
      facility: values.facility,
      allowable_psi: values.allowable_psi,
      options: values.options ?? {},
    });
    if (!parsed.success) {
      assignFieldErrors(formMethods, parsed.error.flatten().fieldErrors);
      return;
    }

    try {
      await patchProject({
        current_step: 2,
        inputs: {
          facility: parsed.data.facility,
          method: values.method,
          allowable_psi: parsed.data.allowable_psi,
          options: parsed.data.options,
        },
      });
      goToStep(3);
    } catch (e) {
      setStepErrors([formatErrorMessage(e, 'Could not save your configuration.')]);
    }
  }, [formMethods, goToStep, patchProject]);

  // Step 3 - fixtures, then always run the sizing engine before results.
  const handleStep3Next = React.useCallback(async () => {
    setStepErrors([]);
    setCalculationErrors([]);
    const values = formMethods.getValues();
    const parsed = step4FixturesSchema.safeParse({ fixtures: values.fixtures ?? [] });
    if (!parsed.success) {
      const flat = parsed.error.flatten();
      const generic = [...flat.formErrors];
      Object.entries(flat.fieldErrors).forEach(([, errs]) => {
        if (Array.isArray(errs)) generic.push(...errs);
      });
      setStepErrors(generic.length ? generic : ['Add a quantity to at least one fixture.']);
      return;
    }

    const key = project?.key;
    if (!key) {
      setStepErrors(['No active project to update.']);
      return;
    }

    const nonZero = parsed.data.fixtures.filter((row) => row.quantity > 0);

    // Max Use facilities (gym/club/industrial/maximum flow) size on
    // Σ quantity × flow_rate, so every fixture the user adds needs a
    // positive GPM value - otherwise the engine derives zero demand and
    // returns no valve. Enforce it here before we spend a calculate call.
    if (facilityUsesFlowRate(values.facility)) {
      const missingFlow = nonZero.filter((row) => !(typeof row.flow_rate === 'number' && row.flow_rate > 0));
      if (missingFlow.length > 0) {
        parsed.data.fixtures.forEach((row, index) => {
          if (row.quantity > 0 && !(typeof row.flow_rate === 'number' && row.flow_rate > 0)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formMethods.setError(`fixtures.${index}.flow_rate` as any, {
              message: 'Enter a flow rate (GPM).',
            });
          }
        });
        setStepErrors([
          'Enter a flow rate (GPM) for each fixture you have added — this facility sizes on maximum use.',
        ]);
        return;
      }
    }

    try {
      await patchProject({
        current_step: 3,
        inputs: { fixtures: nonZero },
      });
    } catch (e) {
      setStepErrors([formatErrorMessage(e, 'Could not save your fixtures.')]);
      return;
    }

    try {
      const updated = await projectApi.calculateProject(key, { method: calculationMethod });
      setProject((prev) => (prev ? { ...prev, ...updated } : updated));
      const resolved = updated.result ? resolveResultProducts(updated.result) : null;
      if (resolved) {
        const family = defaultResultValveFamily(resolved, updated.inputs?.family ?? updated.valve_family ?? null);
        setSelectedValveFamily(family);
        // The valve family is no longer its own wizard step, so persist the
        // defaulted choice now (unless the project already has one saved) so
        // the spec sheet and customer portal reflect the family shown on the
        // results page. Non-fatal if it fails - the toggle re-saves on click.
        const hasSavedFamily =
          updated.inputs?.family === TempControlValveFamily.Digital ||
          updated.inputs?.family === TempControlValveFamily.Conventional;
        if (!hasSavedFamily) {
          try {
            const withFamily = await projectApi.updateProject(key, { inputs: { family } });
            setProject((prev) => (prev ? { ...prev, ...withFamily } : withFamily));
            formMethods.setValue('family', family, { shouldDirty: false });
          } catch {
            // Ignore: results still show the default; it just isn't persisted
            // until the user interacts with the valve-family toggle.
          }
        }
      }
    } catch (e) {
      const messages = extractValidationMessages(e);
      if (messages.length > 0) {
        setCalculationErrors(messages);
      } else {
        setCalculationErrors([formatErrorMessage(e, 'Could not run the sizing engine.')]);
      }
    }

    setHighestStepReached(3);
    setPhase('results');
  }, [calculationMethod, formMethods, patchProject, project?.key, projectApi]);

  const handleStartOver = React.useCallback(() => {
    if (project?.key) {
      clearProjectStorage(project.key);
    } else {
      clearActiveProjectKey();
    }
    setProject(null);
    setStepErrors([]);
    setCalculationErrors([]);
    setBootstrapError(null);
    formMethods.reset(projectToFormValues(null));
    setCurrentStep(1);
    setHighestStepReached(1);
    setPhase('wizard');
    setCalculationMethod(TempControlCalculationMethod.Hunters);
    setSelectedValveFamily(TempControlValveFamily.Digital);
    clearProjectQueryParam(history, location);
    // Drop the just-cleared row from the panel without waiting for
    // the next refetch tick.
    void draftHistory.refresh();
  }, [draftHistory, formMethods, history, location, project]);

  /** Click "Edit project" on the results page - return to the fixtures step. */
  const handleEditFromResults = React.useCallback(() => {
    setPhase('wizard');
    setCurrentStep(3);
  }, []);

  const handleSelectValveFamily = React.useCallback(
    async (family: TempControlValveFamily) => {
      const key = project?.key;
      if (!key || family === selectedValveFamily) return;

      const previousFamily = selectedValveFamily;
      setSelectedValveFamily(family);

      try {
        const updated = await projectApi.updateProject(key, { inputs: { family } });
        setProject((prev) => (prev ? { ...prev, ...updated } : updated));
        formMethods.setValue('family', family, { shouldDirty: false });
      } catch (e) {
        setSelectedValveFamily(previousFamily);
        toast.notify({
          type: 'error',
          title: 'Could not save valve choice',
          message: formatErrorMessage(e, 'Please try again.'),
          autoClose: true,
        });
      }
    },
    [formMethods, project?.key, projectApi, selectedValveFamily, toast]
  );

  /**
   * Toggle an add-on option from the results-page "Available Options"
   * accordion. Options only build the part-number suffix server-side (they do
   * not affect sizing), so we persist the new options set and re-run the
   * calculation to rebuild the SKU. The checkbox updates optimistically and
   * reverts if the round-trip fails.
   */
  const handleToggleOption = React.useCallback(
    async (optionKey: AddOnOptionKey, checked: boolean) => {
      if (!project || !('inputs' in project)) return;
      const key = project.key;

      const currentOptions = (project.inputs?.options ?? {}) as Record<string, boolean>;
      const nextOptions = { ...currentOptions, [optionKey]: checked };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formMethods.setValue(`options.${optionKey}` as any, checked, { shouldDirty: false });
      setProject((prev) =>
        prev && 'inputs' in prev ? { ...prev, inputs: { ...prev.inputs, options: nextOptions } } : prev
      );

      try {
        await projectApi.updateProject(key, { inputs: { options: nextOptions } });
        const updated = await projectApi.calculateProject(key, { method: calculationMethod });
        setProject((prev) => (prev ? { ...prev, ...updated } : updated));
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formMethods.setValue(`options.${optionKey}` as any, !checked, { shouldDirty: false });
        setProject((prev) =>
          prev && 'inputs' in prev ? { ...prev, inputs: { ...prev.inputs, options: currentOptions } } : prev
        );
        toast.notify({
          type: 'error',
          title: 'Could not update options',
          message: formatErrorMessage(e, 'Please try again.'),
          autoClose: true,
        });
      }
    },
    [calculationMethod, formMethods, project, projectApi, toast]
  );

  const handleOpenDownload = React.useCallback((prefill?: Partial<DownloadFormValues>) => {
    setDownloadPrefill(prefill ?? {});
    setIsDownloadModalOpen(true);
  }, []);

  const handleCloseDownload = React.useCallback(() => {
    setIsDownloadModalOpen(false);
    setDownloadPrefill({});
  }, []);

  /**
   * Submit handler for the lead-capture modal. Mirrors the SPB
   * publish flow now: POST /download persists customer details and
   * dispatches the render to the queue worker; we poll the show
   * endpoint until `pdf_status === 'ready'`, then GET /download to
   * stream the cached file from the private filesystem and trigger
   * the browser's native download via an in-memory anchor click.
   *
   * The modal stays open in "preparing" state for the duration of
   * the render so the user gets visible feedback (renders are
   * usually seconds, not instantaneous).
   */
  const handleDownloadSubmit = React.useCallback(
    async (values: DownloadFormValues) => {
      const key = project?.key;
      if (!key) {
        throw new Error('No active project to download.');
      }

      setIsPdfRendering(true);
      try {
        const { blob, filename } = await projectApi.downloadProject(key, {
          customer_name: values.customer_name,
          customer_email: values.customer_email,
          customer_company: values.customer_company || undefined,
        });

        const url = (window.URL || window.webkitURL).createObjectURL(blob);
        const link = document.createElement('a');
        link.rel = 'noopener';
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => (window.URL || window.webkitURL).revokeObjectURL(url), 40000);

        setProject((prev) =>
          prev
            ? {
                ...prev,
                customer_name: values.customer_name,
                customer_email: values.customer_email,
                customer_company: values.customer_company ?? null,
              }
            : prev
        );
        setIsDownloadModalOpen(false);
        toast.notify({
          type: 'success',
          title: 'Your PDF is ready',
          message: 'We saved your details on this project so you can grab the PDF again later.',
          autoClose: true,
        });
      } finally {
        setIsPdfRendering(false);
      }
    },
    [project, projectApi, toast]
  );

  // ---- Rendering ----

  if (isBootstrapping) {
    return (
      <>
        <WizardHero />
        <section className="c-block c-block--spacing-t c-block--spacing-b">
          <div className="o-container-fluid">
            <p>Hang tight, we're picking up where you left off…</p>
          </div>
        </section>
      </>
    );
  }

  if (phase === 'results' && project) {
    const projectAsFull = project as TempControlProject;
    return (
      <FormProvider {...formMethods} hasFloatingLabels>
        <ResultsPage
          project={projectAsFull}
          selectedMethod={calculationMethod}
          onSelectMethod={setCalculationMethod}
          selectedValveFamily={selectedValveFamily}
          onSelectValveFamily={handleSelectValveFamily}
          isSavingValveFamily={projectApi.isUpdating}
          onToggleOption={handleToggleOption}
          isUpdatingOptions={projectApi.isUpdating || projectApi.isCalculating}
          onEdit={handleEditFromResults}
          onStartNew={handleStartOver}
          onDownload={handleOpenDownload}
          isDownloading={projectApi.isDownloading}
          calculationErrors={calculationErrors}
        />
        <DownloadModal
          isOpen={isDownloadModalOpen}
          isSubmitting={projectApi.isDownloading || isPdfRendering}
          isRendering={isPdfRendering}
          defaultValues={{
            customer_name: downloadPrefill.customer_name ?? projectAsFull.customer_name ?? '',
            customer_email: downloadPrefill.customer_email ?? projectAsFull.customer_email ?? '',
            customer_company: downloadPrefill.customer_company ?? projectAsFull.customer_company ?? '',
          }}
          onRequestClose={handleCloseDownload}
          onSubmit={handleDownloadSubmit}
        />
      </FormProvider>
    );
  }

  const stepCopy = wizardStepCopy[currentStep];
  const handlers: Record<WizardStepId, () => void | Promise<void>> = {
    1: handleStep1Next,
    2: handleStep2Next,
    3: handleStep3Next,
  };
  // Step 1 reads "Start project" on the very first save (the click that
  // actually creates the row); once the project exists, the same button
  // just patches the rename and advances, so the verb softens. Step 3
  // (fixtures) wraps the wizard, hence "Get results".
  const nextLabel =
    currentStep === 3
      ? 'Get Results'
      : currentStep === 1 && !project
      ? 'Start Project'
      : currentStep === 1
      ? 'Save & Continue'
      : 'Next';
  const introCallout =
    currentStep === 1 ? (
      <HistoryPanel
        layout="desktop"
        entries={draftHistory.entries}
        activeProjectKey={project?.key}
        onSelect={(key) => void handleSwitchProject(key)}
        isLoading={draftHistory.isLoading}
        viewAllUrl={portalProjectsUrl}
      />
    ) : (
      renderIntroCallout(currentStep)
    );
  const mobileCallout =
    currentStep === 1 ? (
      <HistoryPanel
        layout="mobile"
        entries={draftHistory.entries}
        activeProjectKey={project?.key}
        onSelect={(key) => void handleSwitchProject(key)}
        isLoading={draftHistory.isLoading}
        viewAllUrl={portalProjectsUrl}
      />
    ) : currentStep === 2 ? (
      renderIntroCallout(2, 'mobile')
    ) : undefined;
  const contentMaxWidth = currentStep === 1 ? '400' : '600';
  const isSubmitting = projectApi.isCreating || projectApi.isUpdating || projectApi.isCalculating;
  const useFloatingLabels = currentStep === 1 || currentStep === 2;

  return (
    <FormProvider {...formMethods} hasFloatingLabels={useFloatingLabels}>
      <form
        className={classNames({
          'c-form--float-labels': useFloatingLabels,
          'fl-form': useFloatingLabels,
        })}
        onSubmit={(e) => e.preventDefault()}
      >
        <WizardLayout
          currentStep={currentStep}
          introTitle={stepCopy.title}
          introBody={stepCopy.body}
          introCallout={introCallout}
          introVariant={currentStep === 1 ? 'intro' : 'step'}
          contentMaxWidth={contentMaxWidth}
          mobileCallout={mobileCallout}
          onBack={currentStep > 1 ? handleBack : undefined}
          banner={
            bootstrapError ? (
              <ErrorList title="We started a fresh wizard:" messages={[bootstrapError]} />
            ) : stepErrors.length > 0 ? (
              <ErrorList messages={stepErrors} />
            ) : null
          }
          footer={
            currentStep === 3 ? undefined : (
              <WizardFooter
                onNext={() => void handlers[currentStep]()}
                isSubmitting={isSubmitting}
                nextLabel={nextLabel}
                isLarge={currentStep === 1}
              />
            )
          }
        >
          {currentStep === 3 ? (
            <Step4Fixtures onNext={() => void handleStep3Next()} isSubmitting={isSubmitting} nextLabel={nextLabel} />
          ) : (
            renderStepBody(currentStep)
          )}
        </WizardLayout>
      </form>
    </FormProvider>
  );
};

const renderIntroCallout = (step: WizardStepId, layout: 'desktop' | 'mobile' = 'desktop'): React.ReactNode => {
  switch (step) {
    case 2:
      return (
        <InfoCallout
          layout={layout}
          title="Accuracy Matters"
          imageSrc={`${wordpressData.themeAssetsUrl}/images/content/tempcontrol-step2.jpg`}
        >
          <p>
            The correct facility type determines the calculation method, and additional options affect the final valve
            selection.
          </p>
          <p>Your selections save automatically and you can make changes at any point before final calculation.</p>
        </InfoCallout>
      );
    default:
      return undefined;
  }
};

const renderStepBody = (step: WizardStepId): React.ReactNode => {
  switch (step) {
    case 1:
      return <Step1Project />;
    case 2:
      return <Step3Configuration />;
    // Step 3 (fixtures) is rendered directly in the layout, not here.
    default:
      return null;
  }
};

// ---- Helpers ----

const clampStep = (raw: number): WizardStepId => {
  if (raw <= 1) return 1;
  if (raw === 2) return 2;
  return 3;
};

const setProjectQueryParam = (
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>,
  key: string
) => {
  const params = new URLSearchParams(location.search);
  if (params.get(PROJECT_QUERY_PARAM) === key) return;
  params.set(PROJECT_QUERY_PARAM, key);
  history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
};

const clearProjectQueryParam = (history: ReturnType<typeof useHistory>, location: ReturnType<typeof useLocation>) => {
  const params = new URLSearchParams(location.search);
  if (!params.has(PROJECT_QUERY_PARAM)) return;
  params.delete(PROJECT_QUERY_PARAM);
  const search = params.toString();
  history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
};

const assignFieldErrors = <T extends WizardFormValues>(
  form: ReturnType<typeof useForm<T>>,
  fieldErrors: Record<string, string[] | undefined>
) => {
  Object.entries(fieldErrors).forEach(([name, errors]) => {
    if (errors && errors.length > 0) {
      form.setError(name as any, { message: errors[0] });
    }
  });
};

const formatErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = (error as AxiosError<{ message?: string }>).response?.data;
    if (data && typeof data.message === 'string' && data.message.length > 0) return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const extractValidationMessages = (error: unknown): string[] => {
  if (!axios.isAxiosError(error)) return [];
  const data = (error as AxiosError<{ message?: string; errors?: Record<string, string[]> }>).response?.data;
  if (!data?.errors) return [];
  return Object.values(data.errors).flat();
};

export default Wizard;
